import { useState, useEffect, useCallback } from 'react';
import pushNotifications from 'src/utils/pushNotifications';
import { notificationApi } from 'src/utils/notificationApi';

/**
 * Custom hook for managing push notification subscriptions
 */
function usePushNotification() {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Check support and permission status
   */
  useEffect(() => {
    const checkStatus = async () => {
      const supported = pushNotifications.isPushNotificationSupported();
      setIsSupported(supported);

      if (supported) {
        const currentPermission = pushNotifications.getNotificationPermission();
        setPermission(currentPermission);

        const existingSubscription = await pushNotifications.getExistingSubscription();
        setSubscription(existingSubscription);
        setIsSubscribed(!!existingSubscription);
      }
    };

    checkStatus();
  }, []);

  /**
   * Request permission and subscribe to push notifications
   */
  const subscribe = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Request permission
      const newPermission = await pushNotifications.requestNotificationPermission();
      setPermission(newPermission);

      if (newPermission === 'denied') {
        throw new Error('Notification permission denied');
      }

      // Register service worker
      await pushNotifications.registerServiceWorker();

      // Subscribe to push
      const newSubscription = await pushNotifications.subscribeToPushNotifications();
      setSubscription(newSubscription);
      setIsSubscribed(true);

      // Send to backend
      const subscriptionData = pushNotifications.getSubscriptionJSON(newSubscription);
      const deviceType = pushNotifications.getBrowserType();

      await notificationApi.registerDevice({
        ...subscriptionData,
        device_type: deviceType
      });

      return newSubscription;
    } catch (err) {
      console.error('Error subscribing to push notifications:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      await pushNotifications.unsubscribeFromPushNotifications();
      setSubscription(null);
      setIsSubscribed(false);

      return true;
    } catch (err) {
      console.error('Error unsubscribing from push notifications:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Show a test notification
   */
  const sendTestNotification = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await pushNotifications.showTestNotification();
      
      // Update permission state if it changed
      const currentPermission = pushNotifications.getNotificationPermission();
      setPermission(currentPermission);

      return result;
    } catch (err) {
      console.error('Error sending test notification:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    isSupported,
    permission,
    subscription,
    isSubscribed,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
}

export default usePushNotification;
